import { FilterSet, Filter, FilterOption, SearchFilter } from '#server/models';
import { EARLIEST_DATE, LATEST_DATE } from '@/store';

type ConversionError = {
  type: 'missing_field' | 'invalid_format' | 'empty_value';
  message: string;
};

const logConversionError = (error: ConversionError, filterTitle: string): void => {
  if (process.env.NODE_ENV === 'development') {
    console.warn(`⚠️ [FRONTEND-CONVERT] Filter "${filterTitle}" error - ${error.type}: ${error.message}`);
  }
};

/**
 * Type guards
 */
const isStringTuple = (value: unknown): value is [string, string] =>
  Array.isArray(value) && value.length === 2 && value.every((v) => typeof v === 'string');

const isNumberTuple = (value: unknown): value is [number, number] =>
  Array.isArray(value) && value.length === 2 && value.every((v) => typeof v === 'number');

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.length > 0 && value.every((v) => typeof v === 'string');

const isDateValue = (value: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(value);

const isDateRange = (value: [string, string]): boolean =>
  (isDateValue(value[0]) && isDateValue(value[1])) ||
  (isDateValue(value[0]) && value[1] === '') ||
  (isDateValue(value[1]) && value[0] === '');

const isDualFieldConfig = (field?: string | { start: string; end: string }): field is { start: string; end: string } =>
  typeof field === 'object' && field !== null && 'start' in field && 'end' in field;

const isSingleFieldConfig = (field?: string | { start: string; end: string }): field is string =>
  typeof field === 'string';

/**
 * Extract values based on FilterOption mapping
 */
const extractFilterValues = (
  selectedValues: string[],
  filterOptions?: FilterOption[],
): { values: string[]; selectedOptions: FilterOption[] } => {
  if (!filterOptions) {
    return { values: selectedValues, selectedOptions: [] };
  }

  const titleToOption = new Map(filterOptions.map((o) => [o.title, o] as const));
  const values: string[] = [];
  const selectedOptions: FilterOption[] = [];

  for (const sel of selectedValues) {
    const option = titleToOption.get(sel);
    if (!option) {
      logConversionError({ type: 'invalid_format', message: `No mapping for ${sel}` }, 'extractFilterValues');
      continue;
    }
    selectedOptions.push(option);
    if (Array.isArray(option.value)) {
      values.push(...option.value);
    } else {
      values.push(option.value);
    }
  }

  return { values: Array.from(new Set(values)), selectedOptions };
};

/**
 * Get varyBy flags from selected options
 */
const getVaryByFlags = (selectedOptions: FilterOption[]): Partial<SearchFilter> => {
  const flags: Partial<SearchFilter> = {};
  if (selectedOptions.some((o) => o.varyByYear)) flags.varyByYear = true;
  if (selectedOptions.some((o) => o.varyByLocation)) flags.varyByLocation = true;
  if (selectedOptions.some((o) => o.varyByStudyPeriod)) flags.varyByStudyPeriod = true;
  return flags;
};

/**
 * Create nested filters from configuration
 */
const createNestedFilters = (
  nestedConfig: NonNullable<Filter['nested'] | FilterOption['nested']>,
  mainFilter: Partial<SearchFilter>,
): SearchFilter['nested'] => {
  return {
    path: nestedConfig.path,
    filters: [
      mainFilter as SearchFilter,
      ...nestedConfig.filters.map((nf): SearchFilter => {
        const filter: SearchFilter = { key: nf.field };

        // Add varyBy flags
        if (nf.varyByYear) filter.varyByYear = true;
        if (nf.varyByLocation) filter.varyByLocation = true;
        if (nf.varyByStudyPeriod) filter.varyByStudyPeriod = true;

        // Add value if defined
        if (nf.value !== undefined) {
          filter.valueExact = Array.isArray(nf.value) ? nf.value[0] : nf.value;
        }

        return filter;
      }),
    ],
  };
};

/**
 * Add nested structure to a filter if needed
 */
const addNestedIfNeeded = (result: SearchFilter, filterDef: Filter, selectedOptions: FilterOption[] = []): void => {
  // Check for nested in selected options first
  const nestedOption = selectedOptions.find((o) => o.nested);
  if (nestedOption?.nested) {
    result.nested = createNestedFilters(nestedOption.nested, { ...result });
    return;
  }

  // Check for nested in filter definition
  if (filterDef.nested) {
    result.nested = createNestedFilters(filterDef.nested, { ...result });
  }
};

/**
 * Handle date range filters
 */
const handleDateRange = (value: [string, string], filterTitle: string, filterDef: Filter): SearchFilter | null => {
  if (isDualFieldConfig(filterDef.field)) {
    const result: SearchFilter = {
      key: filterTitle,
      metadataField: filterDef.metadataField,
      valueBetweenOverlap: {
        min: value[0] || EARLIEST_DATE,
        max: value[1] || LATEST_DATE,
        startField: filterDef.field.start,
        endField: filterDef.field.end,
      },
    };
    addNestedIfNeeded(result, filterDef);
    return result;
  }

  if (isSingleFieldConfig(filterDef.field)) {
    const result: SearchFilter = {
      key: filterDef.field,
      metadataField: filterDef.metadataField,
      valueBetween: { min: value[0] || EARLIEST_DATE, max: value[1] || LATEST_DATE },
    };
    addNestedIfNeeded(result, filterDef);
    return result;
  }

  return null;
};

/**
 * Handle numeric range filters
 */
const handleNumberRange = (value: [number, number], filterDef: Filter): SearchFilter | null => {
  if (!isSingleFieldConfig(filterDef.field)) return null;

  const result: SearchFilter = {
    key: filterDef.field,
    metadataField: filterDef.metadataField,
    valueBetween: { min: String(value[0]), max: String(value[1]) },
  };
  addNestedIfNeeded(result, filterDef);
  return result;
};

/**
 * Handle multi-select filters
 */
const handleMultiSelect = (value: string[], filterDef: Filter): SearchFilter | SearchFilter[] | null => {
  if (!isSingleFieldConfig(filterDef.field)) return null;

  const { values: mappedValues, selectedOptions } = extractFilterValues(value, filterDef.options);
  if (mappedValues.length === 0) return null;

  // Handle complex nested grouping (for cases like Start date with different years)
  if (selectedOptions.some((o) => o.nested)) {
    // Group by nested filter signature
    type GroupKey = string;
    const groups = new Map<GroupKey, { values: string[]; nested: FilterOption['nested'] }>();

    for (const opt of selectedOptions) {
      const optValues = Array.isArray(opt.value) ? opt.value : [opt.value];
      const nested = opt.nested;
      if (!nested) continue;

      const sig = JSON.stringify(
        nested.filters
          .map((f) => ({
            field: f.field,
            value: f.value ?? null,
          }))
          .sort((a, b) => a.field.localeCompare(b.field)),
      );

      const entry = groups.get(sig) ?? { values: [], nested };
      entry.values.push(...optValues);
      groups.set(sig, entry);
    }

    const filters: SearchFilter[] = [];
    for (const { values, nested } of groups.values()) {
      const uniqueValues = Array.from(new Set(values));

      const filter: SearchFilter = {
        key: filterDef.field,
        metadataField: filterDef.metadataField,
        ...(uniqueValues.length === 1 ? { valueExact: uniqueValues[0] } : { valueIn: uniqueValues }),
        ...getVaryByFlags(selectedOptions),
        nested: {
          path: nested!.path,
          filters: [
            {
              key: filterDef.field,
              ...(uniqueValues.length === 1 ? { valueExact: uniqueValues[0] } : { valueIn: uniqueValues }),
            },
            ...nested!.filters.map((nf): SearchFilter => {
              const filter: SearchFilter = { key: nf.field };

              if (nf.varyByYear) filter.varyByYear = true;
              if (nf.varyByLocation) filter.varyByLocation = true;
              if (nf.varyByStudyPeriod) filter.varyByStudyPeriod = true;

              if (nf.value !== undefined) {
                filter.valueExact = Array.isArray(nf.value) ? nf.value[0] : nf.value;
              }

              return filter;
            }),
          ],
        },
      };

      filters.push(filter);
    }

    return filters;
  }

  // Simple multi-select without complex nested grouping
  const result: SearchFilter = {
    key: filterDef.field,
    metadataField: filterDef.metadataField,
    ...(mappedValues.length === 1 ? { valueExact: mappedValues[0] } : { valueIn: mappedValues }),
    ...getVaryByFlags(selectedOptions),
  };

  addNestedIfNeeded(result, filterDef, selectedOptions);
  return result;
};

/**
 * Handle single value filters
 */
const handleSingleValue = (value: string, filterDef: Filter): SearchFilter | null => {
  if (!isSingleFieldConfig(filterDef.field)) return null;

  const { values: mappedValues, selectedOptions } = extractFilterValues([value], filterDef.options);
  const finalValue = mappedValues.length > 0 ? mappedValues[0] : value;

  const result: SearchFilter = {
    key: filterDef.field,
    metadataField: filterDef.metadataField,
    valueExact: finalValue,
    ...getVaryByFlags(selectedOptions),
  };

  addNestedIfNeeded(result, filterDef, selectedOptions);
  return result;
};

/**
 * Convert one filter value
 */
const convertFilterValue = (
  filterTitle: string,
  value: string[] | [number, number] | [string, string] | string,
  filterDef: Filter,
): SearchFilter | SearchFilter[] | null => {
  if (!filterDef.field) {
    logConversionError({ type: 'missing_field', message: 'No field configuration' }, filterTitle);
    return null;
  }

  // Date ranges
  if (isStringTuple(value) && isDateRange(value)) {
    return handleDateRange(value, filterTitle, filterDef);
  }

  // Numeric ranges
  if (isNumberTuple(value)) {
    return handleNumberRange(value, filterDef);
  }

  // Multi-select
  if (isStringArray(value)) {
    return handleMultiSelect(value, filterDef);
  }

  // Single value
  if (typeof value === 'string') {
    return handleSingleValue(value, filterDef);
  }

  logConversionError({ type: 'invalid_format', message: 'Unsupported filter value format' }, filterTitle);
  return null;
};

/**
 * Main converter
 */
export const convertActiveFiltersToSearchFilters = (
  activeFilters: Record<string, string[] | [number, number] | [string, string] | string>,
  currentFilterSet: FilterSet | null,
): SearchFilter[] => {
  if (!currentFilterSet) {
    logConversionError(
      { type: 'missing_field', message: 'No currentFilterSet provided' },
      'convertActiveFiltersToSearchFilters',
    );
    return [];
  }

  const filterMap = new Map(currentFilterSet.map((f) => [f.title, f]));
  const result: SearchFilter[] = [];

  for (const [title, value] of Object.entries(activeFilters)) {
    const def = filterMap.get(title);
    if (!def) {
      logConversionError({ type: 'missing_field', message: 'No definition found' }, title);
      continue;
    }
    if (!value || (Array.isArray(value) && value.length === 0)) continue;

    const converted = convertFilterValue(title, value, def);
    if (Array.isArray(converted)) {
      result.push(...converted);
    } else if (converted) {
      result.push(converted);
    }
  }

  return result;
};
